package br.com.vvrefeicoes.portal.navigation

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.navigation.NavHostController
import androidx.navigation.compose.NavHost
import androidx.navigation.compose.composable
import androidx.navigation.toRoute
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.feature.audit.AuditScreen
import br.com.vvrefeicoes.portal.feature.companies.CompaniesScreen
import br.com.vvrefeicoes.portal.feature.dashboard.DashboardScreen
import br.com.vvrefeicoes.portal.feature.foodpass.FoodPassBillingScreen
import br.com.vvrefeicoes.portal.feature.foodpass.FoodPassClientsScreen
import br.com.vvrefeicoes.portal.feature.foodpass.FoodPassConsumptionsScreen
import br.com.vvrefeicoes.portal.feature.forms.FormBuilderScreen
import br.com.vvrefeicoes.portal.feature.forms.FormDefinition
import br.com.vvrefeicoes.portal.feature.forms.FormReadScreen
import br.com.vvrefeicoes.portal.feature.forms.FormsListScreen
import br.com.vvrefeicoes.portal.feature.forms.hasAllFormSectors
import br.com.vvrefeicoes.portal.feature.forms.opensInReadMode
import br.com.vvrefeicoes.portal.feature.guides.GuideDetailScreen
import br.com.vvrefeicoes.portal.feature.guides.GuideFormScreen
import br.com.vvrefeicoes.portal.feature.guides.GuidesAndManualsScreen
import br.com.vvrefeicoes.portal.feature.juridico.JuridicoEmpresasScreen
import br.com.vvrefeicoes.portal.feature.juridico.JuridicoProcessosScreen
import br.com.vvrefeicoes.portal.feature.legal.PrivacyPolicyScreen
import br.com.vvrefeicoes.portal.feature.monitor.PublicMonitorScreen
import br.com.vvrefeicoes.portal.feature.notifications.AnnouncementsManagementScreen
import br.com.vvrefeicoes.portal.feature.notifications.NotificationsScreen
import br.com.vvrefeicoes.portal.feature.operacional.OperationalEntriesScreen
import br.com.vvrefeicoes.portal.feature.operacional.OperationalPanelScreen
import br.com.vvrefeicoes.portal.feature.orgchart.OrgChartScreen
import br.com.vvrefeicoes.portal.feature.placeholder.PlaceholderScreen
import br.com.vvrefeicoes.portal.feature.purchases.PurchasesEntriesScreen
import br.com.vvrefeicoes.portal.feature.purchases.PurchasesPanelScreen
import br.com.vvrefeicoes.portal.feature.rh.RhEntriesScreen
import br.com.vvrefeicoes.portal.feature.rh.RhPanelScreen
import br.com.vvrefeicoes.portal.feature.rmp.RmpNewScreen
import br.com.vvrefeicoes.portal.feature.rmp.RmpScreen
import br.com.vvrefeicoes.portal.feature.rmp.RmpSyncScreen
import br.com.vvrefeicoes.portal.feature.satisfaction.SatisfactionOperationStatusScreen
import br.com.vvrefeicoes.portal.feature.satisfaction.SatisfactionReportsScreen
import br.com.vvrefeicoes.portal.feature.social.SocialConnectionsScreen
import br.com.vvrefeicoes.portal.feature.users.UsersScreen

@Composable
fun AdminNavHost(
    navController: NavHostController,
    onNavigate: (Any) -> Unit,
) {
    val onGoHome = { onNavigate(HomeRoute) }
    NavHost(
        navController = navController,
        startDestination = HomeRoute,
    ) {
        composable<HomeRoute> { DashboardScreen(onNavigate = onNavigate) }
        composable<AdminCompaniesRoute> {
            RequirePermission(PermissionKeys.COMPANIES, onGoHome) { CompaniesScreen(onNavigate = onNavigate) }
        }
        composable<JuridicoEmpresasRoute> {
            RequirePermission(PermissionKeys.CNPJ_EMPRESAS, onGoHome) { JuridicoEmpresasScreen() }
        }
        composable<JuridicoOfficersCourtsRoute> {
            RequirePermission(PermissionKeys.JURIDICO, onGoHome) { JuridicoProcessosScreen() }
        }
        composable<AdminUsersRoute> {
            RequirePermission(PermissionKeys.USERS, onGoHome) { UsersScreen(onNavigate = onNavigate) }
        }
        composable<AdminAuditRoute> {
            RequirePermission(PermissionKeys.AUDIT, onGoHome) { AuditScreen() }
        }
        composable<OrgChartRoute> {
            RequirePermission(PermissionKeys.ORG_CHART, onGoHome) { OrgChartScreen(onNavigate = onNavigate) }
        }
        composable<NotificationsRoute> { NotificationsScreen() }
        composable<ProfileRoute> { PlaceholderScreen("Perfil") }
        composable<AnnouncementsRoute> {
            RequirePermission(PermissionKeys.ANNOUNCEMENTS, onGoHome) { AnnouncementsManagementScreen() }
        }
        composable<SystemHealthRoute> {
            RequirePermission(PermissionKeys.SYSTEM_HEALTH, onGoHome) { PlaceholderScreen("Saúde do sistema") }
        }
        composable<GuidesAndManualsRoute> {
            RequirePermission(PermissionKeys.GUIDES_AND_MANUALS, onGoHome) {
                GuidesAndManualsScreen(
                    onOpen = { id -> navController.navigate(GuideDetailRoute(id)) { launchSingleTop = true } },
                    onCreate = { navController.navigate(GuideCreateRoute) { launchSingleTop = true } },
                    onEdit = { id -> navController.navigate(GuideEditRoute(id)) { launchSingleTop = true } },
                )
            }
        }
        composable<GuideDetailRoute> { entry ->
            RequirePermission(PermissionKeys.GUIDES_AND_MANUALS, onGoHome) {
                GuideDetailScreen(
                    guideId = entry.toRoute<GuideDetailRoute>().id,
                    onBack = { navController.popBackStack() },
                    onEdit = { id ->
                        navController.navigate(GuideEditRoute(id)) {
                            popUpTo<GuideDetailRoute> { inclusive = true }
                            launchSingleTop = true
                        }
                    },
                )
            }
        }
        composable<GuideCreateRoute> {
            RequirePermission(PermissionKeys.GUIDES_AND_MANUALS, onGoHome) {
                GuideFormScreen(
                    guideId = null,
                    onBack = { navController.popBackStack() },
                    onSaved = { id ->
                        navController.navigate(GuideDetailRoute(id)) {
                            popUpTo<GuidesAndManualsRoute> { inclusive = false }
                            launchSingleTop = true
                        }
                    },
                    onDraftSaved = {},
                    onDeleted = {
                        navController.navigate(GuidesAndManualsRoute) {
                            popUpTo<GuidesAndManualsRoute> { inclusive = true }
                            launchSingleTop = true
                        }
                    },
                )
            }
        }
        composable<GuideEditRoute> { entry ->
            val guideId = entry.toRoute<GuideEditRoute>().id
            RequirePermission(PermissionKeys.GUIDES_AND_MANUALS, onGoHome) {
                GuideFormScreen(
                    guideId = guideId,
                    onBack = { navController.popBackStack() },
                    onSaved = { id ->
                        navController.navigate(GuideDetailRoute(id)) {
                            popUpTo<GuidesAndManualsRoute> { inclusive = false }
                            launchSingleTop = true
                        }
                    },
                    onDraftSaved = {},
                    onDeleted = {
                        navController.navigate(GuidesAndManualsRoute) {
                            popUpTo<GuidesAndManualsRoute> { inclusive = true }
                            launchSingleTop = true
                        }
                    },
                )
            }
        }
        composable<PrivacyPolicyRoute> { PrivacyPolicyScreen(onBack = { navController.popBackStack() }) }
        composable<PublicMonitorRoute> { entry ->
            PublicMonitorScreen(entry.toRoute<PublicMonitorRoute>().code)
        }
        composable<OperationalPanelRoute> {
            RequirePermission(PermissionKeys.OPERATIONAL_PANEL, onGoHome) { OperationalPanelScreen() }
        }
        composable<OperationalEntriesRoute> {
            RequirePermission(PermissionKeys.OPERATIONAL_ENTRIES, onGoHome) { OperationalEntriesScreen() }
        }
        composable<RhPanelRoute> {
            RequirePermission(PermissionKeys.RH_PANEL, onGoHome) { RhPanelScreen() }
        }
        composable<RhEntriesRoute> {
            RequirePermission(PermissionKeys.RH_ENTRIES, onGoHome) { RhEntriesScreen() }
        }
        composable<CommercialRoute> {
            RequirePermission(PermissionKeys.COMMERCIAL, onGoHome) { PlaceholderScreen("Painel") }
        }
        composable<FoodPassClientsRoute> {
            RequirePermission(PermissionKeys.FOOD_PASS, onGoHome) { FoodPassClientsScreen() }
        }
        composable<FoodPassConsumptionsRoute> {
            RequirePermission(PermissionKeys.FOOD_PASS, onGoHome) { FoodPassConsumptionsScreen() }
        }
        composable<FoodPassBillingRoute> {
            RequirePermission(PermissionKeys.FOOD_PASS, onGoHome) { FoodPassBillingScreen() }
        }
        composable<SocialConnectionsRoute> {
            RequirePermission(PermissionKeys.SOCIAL_CONNECTIONS, onGoHome) { SocialConnectionsScreen() }
        }
        composable<SatisfactionReportsRoute> {
            RequirePermission(PermissionKeys.SATISFACTION, onGoHome) { SatisfactionReportsScreen() }
        }
        composable<SatisfactionOperationStatusRoute> {
            RequirePermission(PermissionKeys.SATISFACTION, onGoHome) { SatisfactionOperationStatusScreen() }
        }
        composable<FormulariosRoute> {
            RequirePermission(PermissionKeys.FORMULARIOS, onGoHome) {
                FormsListScreen(
                    onCreate = { navController.navigate(FormularioCreateRoute) { launchSingleTop = true } },
                    onEdit = { id -> navController.navigate(FormularioEditRoute(id)) { launchSingleTop = true } },
                    onView = { id -> navController.navigate(FormularioViewRoute(id)) { launchSingleTop = true } },
                )
            }
        }
        composable<FormularioCreateRoute> {
            RequirePermission(PermissionKeys.FORMULARIOS, onGoHome) {
                FormBuilderScreen(
                    formId = null,
                    readOnly = false,
                    onBack = { navController.popBackStack() },
                    onSaved = {},
                    onSubmitted = {
                        navController.navigate(FormulariosRoute) {
                            popUpTo<FormulariosRoute> { inclusive = true }
                            launchSingleTop = true
                        }
                    },
                )
            }
        }
        composable<FormularioViewRoute> { entry ->
            RequirePermission(PermissionKeys.FORMULARIOS, onGoHome) {
                FormReadScreen(
                    formId = entry.toRoute<FormularioViewRoute>().id,
                    onBack = { navController.popBackStack() },
                )
            }
        }
        composable<FormularioEditRoute> { entry ->
            RequirePermission(PermissionKeys.FORMULARIOS, onGoHome) {
                FormEditEntryScreen(
                    formId = entry.toRoute<FormularioEditRoute>().id,
                    onBack = { navController.popBackStack() },
                    onSubmitted = {
                        navController.navigate(FormulariosRoute) {
                            popUpTo<FormulariosRoute> { inclusive = true }
                            launchSingleTop = true
                        }
                    },
                )
            }
        }
        composable<PurchasesRoute> {
            RequirePermission(PermissionKeys.PURCHASES, onGoHome) { PurchasesPanelScreen() }
        }
        composable<PurchasesEntriesRoute> {
            RequirePermission(PermissionKeys.PURCHASES_ENTRIES, onGoHome) {
                PurchasesEntriesScreen(onNavigate = onNavigate)
            }
        }
        composable<RmpRoute> {
            RequirePermission(PermissionKeys.RMP, onGoHome) {
                RmpScreen(
                    onRequestNew = { navController.navigate(RmpNewRoute) { launchSingleTop = true } },
                    onEdit = { id -> navController.navigate(RmpEditRoute(id)) { launchSingleTop = true } },
                )
            }
        }
        composable<RmpNewRoute> {
            RequirePermission(PermissionKeys.RMP_REQUEST, onGoHome) {
                RmpNewScreen(onBack = { navController.popBackStack() })
            }
        }
        composable<RmpEditRoute> { entry ->
            RequirePermission(PermissionKeys.RMP, onGoHome) {
                RmpNewScreen(onBack = { navController.popBackStack() }, editId = entry.toRoute<RmpEditRoute>().id)
            }
        }
        composable<RmpSyncRoute> {
            RequirePermission(PermissionKeys.RMP_ADMIN, onGoHome) { RmpSyncScreen() }
        }
    }
}

@Composable
private fun FormEditEntryScreen(
    formId: String,
    onBack: () -> Unit,
    onSubmitted: () -> Unit,
) {
    val repository = LocalAppGraph.current.formsRepository
    val permissions = rememberSessionPermissions()
    val canEdit = permissions.allows(PermissionKeys.FORMULARIOS, PermissionAction.EDIT)
    var form by remember(formId, repository) { mutableStateOf<FormDefinition?>(null) }
    var loading by remember(formId, repository) { mutableStateOf(true) }

    LaunchedEffect(formId, repository) {
        loading = true
        form = runCatching { repository.loadForm(formId) }.getOrNull()
        loading = false
    }

    if (loading) {
        Box(
            modifier = Modifier.fillMaxWidth().padding(48.dp),
            contentAlignment = Alignment.Center,
        ) {
            CircularProgressIndicator()
        }
    } else if (
        form == null ||
        !canEdit ||
        form?.status?.opensInReadMode() == true ||
        form?.sectors?.hasAllFormSectors(permissions) != true
    ) {
        FormReadScreen(formId = formId, onBack = onBack)
    } else {
        FormBuilderScreen(
            formId = formId,
            readOnly = false,
            onBack = onBack,
            onSaved = {},
            onSubmitted = onSubmitted,
        )
    }
}
